import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

import { attendanceApi } from '../../services/api';
import type { GradeSheet, GradeSheetStudent } from '../../types';
import { isLockedByDefault } from '../../utils/gradeSheet';
import { useToast } from '../ui';

export function lockLabel(student: Pick<GradeSheetStudent, 'status'>) {
    if (student.status === null) return 'Chưa điểm danh';
    if (student.status === 'excused') return 'Có phép';
    if (student.status === 'absent') return 'Vắng';
    return '';
}

// Tải bảng của buổi; hàng vắng/chưa điểm danh bị khoá trừ khi đã có số liệu.
export function useGradeSheet(attendanceId: string, hasData: (student: GradeSheetStudent, sheet: GradeSheet) => boolean, refreshKey = 0) {
    const { showToast } = useToast();
    const [sheet, setSheetState] = useState<GradeSheet | null>(null);
    const [loading, setLoading] = useState(true);
    const [unlocked, setUnlocked] = useState<Set<string>>(new Set());

    // Đặt bảng và các hàng được mở trong cùng một lần cập nhật, để hàng không nháy trạng thái khoá.
    const setSheet = useCallback((next: GradeSheet) => {
        const marked = new Set(next.students.filter(student => student.status !== null).map(student => student.id));
        setUnlocked(current => new Set([
            ...next.students
                .filter(student => student.status !== null && (!isLockedByDefault(student) || hasData(student, next)))
                .map(student => student.id),
            // Hàng đã bật lại vẫn mở sau khi lưu hoặc thêm bài.
            ...[...current].filter(id => marked.has(id)),
        ]));
        setSheetState(next);
    }, [hasData]);

    useEffect(() => {
        let cancelled = false;
        // Lần tải lại sau khi lưu điểm danh không che bảng đang nhập.
        if (refreshKey === 0) setLoading(true);
        attendanceApi.getGrades(attendanceId)
            .then(response => { if (!cancelled) setSheet(response.data.data); })
            .catch((error: any) => { if (!cancelled) showToast(error?.message || 'Không tải được bảng điểm của buổi.', 'error'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [attendanceId, refreshKey, setSheet, showToast]);

    return { sheet, setSheet, loading, unlocked, setUnlocked };
}

// Enter nhảy xuống ô cùng cột của học sinh kế tiếp còn mở.
export function focusNextRow(input: HTMLInputElement) {
    const table = input.closest('table');
    const row = Number(input.dataset.sheetRow);
    const col = input.dataset.sheetCol;
    if (!table || Number.isNaN(row) || !col) return;
    const candidates = Array.from(table.querySelectorAll<HTMLInputElement>(`input[data-sheet-col="${col}"]`))
        .filter(candidate => Number(candidate.dataset.sheetRow) > row && !candidate.disabled);
    const next = candidates.sort((a, b) => Number(a.dataset.sheetRow) - Number(b.dataset.sheetRow))[0];
    if (next) {
        next.focus();
        next.select();
    } else {
        input.blur();
    }
}

export function SheetNotice({ children }: { children: ReactNode }) {
    return (
        <div className="flex items-start gap-2 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2 text-sm text-warning-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{children}</span>
        </div>
    );
}
